import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable} from 'rxjs';
import {LoggedUser, User} from './logged-user.service';
import {map} from 'rxjs/operators';
import {TimezoneService} from "../services/timezone.service";

export const MANAGE_ACCOUNT = {requiredPermission: 'PERMISSION_MANAGE_ACCOUNT'};
export const MANAGE_AUDITS = {requiredPermission: 'PERMISSION_MANAGE_AUDITS'};
export const MANAGE_ALARMS = {requiredPermission: 'PERMISSION_MANAGE_ALARMS'};
export const VIEW_ALARMS = {requiredPermission: 'PERMISSION_VIEW_ALARMS'};
export const MANAGE_ANALYSIS = {requiredPermission: 'PERMISSION_MANAGE_ANALYSIS'};
export const MANAGE_FIRMWARE = {requiredPermission: 'PERMISSION_MANAGE_FIRMWARE'};
export const MANAGE_GROUPS = {requiredPermission: 'PERMISSION_MANAGE_GROUPS'};
export const MANAGE_MACHINES = {requiredPermission: 'PERMISSION_MANAGE_MACHINES'};
export const MANAGE_ADVANCED_MACHINES = {requiredPermission: 'PERMISSION_MANAGE_ADVANCED_MACHINES'};
export const MANAGE_MODELS = {requiredPermission: 'PERMISSION_MANAGE_MODELS'};
export const MANAGE_ORGANIZATIONS = {requiredPermission: 'PERMISSION_MANAGE_ORGANIZATIONS'};
export const MANAGE_USERS = {requiredPermission: 'PERMISSION_MANAGE_USERS'};

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  readonly notAuthorizedPath = '/not-authorized';
  private readonly DEFAULT_TIMEZONE = 'Europe/Madrid';

  private userRole: string;
  private permissions: string[];
  private eCommerceUser: boolean;
  private userIsRoot: boolean;
  private offset: string;
  private timezone: string;
  private language: string;

  constructor(private router: Router,
              private loggedUser: LoggedUser,
              private timezoneService: TimezoneService) {
    loggedUser.userObservable.subscribe(user => {
      this.userRole = this.extractRole(user);
      if ('NO_ROLE' === this.userRole) {
        this.router.navigate([this.notAuthorizedPath]).finally();
      }
      this.permissions = user.permissions;
      this.eCommerceUser = user.ecommerceUser;
      this.userIsRoot = user.root;
      this.timezoneService.setTimezone(user.timezone ?? this.DEFAULT_TIMEZONE);
      this.offset = user.offset;
      this.timezone = user.timezone;
      this.language = user.language;
    });
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.permissions === undefined) {
      return this.loggedUser.userObservable.asObservable().pipe(
        map((user: User) => {
          return this.hasRequiredPermission(next.data);
        })
      );
    } else {
      return this.hasRequiredPermission(next.data);
    }
  }

  hasRequiredPermission(requiredPermission: any): boolean | UrlTree {
    return (this.permissions.includes(requiredPermission.requiredPermission)) ? true : this.router.parseUrl(this.notAuthorizedPath);
  }

  hasPermission(requiredPermission: string): boolean {
    return this.permissions.includes(requiredPermission);
  }

  extractRole(user: User): string {
    return user.role ?? 'NO_ROLE';
  }

  isECommerce(): boolean {
    return this.eCommerceUser;
  }

  isRoot(): boolean {
    return this.userIsRoot;
  }

  getTimezone(): string {
    return this.timezone;
  }

  getOffset(): string {
    return this.offset;
  }

  getLanguage(): string {
    return this.language;
  }
}
